import { LogOut, Menu, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { useAuth } from '../../lib/AuthContext';
import { visibleNav } from '../../lib/nav';

function Links() {
  const { user } = useAuth();
  const [location] = useLocation();
  return (
    <>
      {visibleNav(user?.roles ?? []).map(({ to, label, icon: Icon }) => {
        const active = location === to;
        return <Link key={to} href={to} className={`flex min-h-11 items-center gap-3 rounded-xl px-3 font-medium ${active ? 'bg-brand-100 text-brand-700' : 'text-muted hover:bg-brand-50'}`}><Icon size={20} />{label}</Link>;
      })}
    </>
  );
}

// The whole shell is viewport-locked (h-dvh overflow-hidden) — the sidebar and bottom tab bar
// are flex siblings of <main>, never inside the scrolling region, so they can't drift with
// content the way they would if the page itself were the scroll container.
export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [location] = useLocation();

  return (
    <div className="flex h-dvh flex-col overflow-hidden lg:flex-row">
      <aside className="hidden h-dvh w-[250px] shrink-0 flex-col overflow-y-auto border-r bg-white p-4 lg:flex">
        <div className="mb-8 px-2">
          <p className="text-xl font-bold text-brand-700">Smart Office</p>
          <p className="text-sm text-muted">{user?.tenantName ?? 'Staff management'}</p>
        </div>
        <nav className="space-y-1"><Links /></nav>
        <button className="btn-secondary mt-auto justify-start" onClick={() => void logout()}><LogOut size={18} />Log out</button>
      </aside>

      <div className="flex min-h-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between border-b bg-white/95 px-4 backdrop-blur lg:hidden">
          <button aria-label="Open menu" className="btn-secondary !px-3" onClick={() => setOpen(true)}><Menu /></button>
          <div className="text-center"><p className="font-bold text-brand-700">Smart Office</p><p className="max-w-44 truncate text-xs text-muted">{user?.name}</p></div>
          <span className="w-11" />
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="mx-auto max-w-7xl p-4 pb-24 sm:p-6 lg:p-8 lg:pb-8">{children}</div>
        </main>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 lg:hidden" onClick={() => setOpen(false)}>
          <aside className="flex h-full w-72 flex-col overflow-y-auto bg-white p-4 shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-6 flex items-center justify-between"><strong>Menu</strong><button aria-label="Close menu" className="btn-secondary !px-3" onClick={() => setOpen(false)}><X /></button></div>
            <nav className="space-y-1" onClick={() => setOpen(false)}><Links /></nav>
            {/* The desktop sidebar's log-out sits in a pane that's hidden below lg, so without this
                there is no way to sign out on a phone — the app's primary device. */}
            <button className="btn-secondary mt-auto shrink-0 justify-start" onClick={() => { setOpen(false); void logout(); }}><LogOut size={18} />Log out</button>
          </aside>
        </div>
      )}

      <nav className="fixed inset-x-0 bottom-0 z-30 flex justify-around border-t bg-white px-1 pb-[env(safe-area-inset-bottom)] lg:hidden">
        {visibleNav(user?.roles ?? []).slice(0, 5).map(({ to, label, icon: Icon }) => (
          <Link key={to} href={to} className={`flex min-h-16 min-w-14 flex-col items-center justify-center gap-1 text-[11px] font-medium ${location === to ? 'text-brand-700' : 'text-muted'}`}><Icon size={21} />{label}</Link>
        ))}
      </nav>
    </div>
  );
}
